import { Link, useLocation } from 'react-router-dom';
import {
    Menu,
    LogOut,
    User,
    ChevronDown,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';
import { useI18n } from '../i18n';
import LanguageSelector from './LanguageSelector';

export default function Navbar() {
    const location = useLocation();
    const { user, logout } = useAuthStore();
    const { toggleSidebar } = useUIStore();
    const { t } = useI18n();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        await logout();
    };

    const isAdmin = user?.role === 'admin';

    const navLinks = [
        { path: '/', label: t.nav.chat, emoji: '💬' },
        { path: '/gallery', label: t.nav.gallery, emoji: '🖼️' },
        { path: '/settings', label: t.nav.settings, emoji: '⚙️' },
    ];

    return (
        <nav className="sticky top-0 z-40 glass border-b border-dark-800">
            <div className="flex items-center justify-between h-14 px-4">
                {/* Left side */}
                <div className="flex items-center gap-4">
                    <button
                        onClick={toggleSidebar}
                        className="btn-icon md:hidden"
                        aria-label="Toggle sidebar"
                    >
                        <Menu className="w-5 h-5" />
                    </button>

                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-500 to-purple-600 flex items-center justify-center">
                            <span className="text-white font-bold text-sm">AI</span>
                        </div>
                        <span className="font-semibold text-dark-100 hidden sm:block">
                            AI Agent
                        </span>
                    </Link>

                    {/* Navigation links - always visible, responsive */}
                    <div className="flex items-center gap-1 ml-2 sm:ml-6">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`px-2 sm:px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${location.pathname === link.path
                                    ? 'bg-dark-800 text-dark-100'
                                    : 'text-dark-400 hover:text-dark-100 hover:bg-dark-800/50'
                                    }`}
                                title={link.label}
                            >
                                <span>{link.emoji}</span>
                                <span className="hidden sm:inline">{link.label}</span>
                            </Link>
                        ))}

                        {/* Admin link */}
                        {isAdmin && (
                            <Link
                                to="/admin"
                                className={`px-2 sm:px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${location.pathname.startsWith('/admin')
                                    ? 'bg-accent-600/20 text-accent-400'
                                    : 'text-accent-400/70 hover:text-accent-400 hover:bg-accent-600/10'
                                    }`}
                                title={t.nav.admin}
                            >
                                <span>🛡️</span>
                                <span className="hidden sm:inline">{t.nav.admin}</span>
                            </Link>
                        )}
                    </div>
                </div>

                {/* Right side - Language selector & User menu */}
                <div className="flex items-center gap-3">
                    <LanguageSelector />

                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-dark-800 transition-colors"
                        >
                            <div className="w-7 h-7 rounded-full bg-dark-700 flex items-center justify-center">
                                <User className="w-4 h-4 text-dark-300" />
                            </div>
                            <span className="text-sm font-medium text-dark-200 hidden sm:block">
                                {user?.username}
                            </span>
                            <ChevronDown className="w-4 h-4 text-dark-400" />
                        </button>

                        {/* Dropdown */}
                        {dropdownOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-dark-900 border border-dark-800 rounded-xl shadow-xl py-1 animate-in">
                                <div className="px-4 py-2 border-b border-dark-800">
                                    <p className="text-sm font-medium text-dark-100">{user?.username}</p>
                                    <p className="text-xs text-dark-500 capitalize">{user?.role}</p>
                                </div>

                                {/* Mobile navigation - now visible in main bar, so this is redundant but kept for dropdown */}
                                <div className="sm:hidden py-1 border-b border-dark-800">
                                    {navLinks.map((link) => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            onClick={() => setDropdownOpen(false)}
                                            className="flex items-center gap-2 px-4 py-2 text-sm text-dark-300 hover:bg-dark-800"
                                        >
                                            <span>{link.emoji}</span>
                                            {link.label}
                                        </Link>
                                    ))}
                                    {isAdmin && (
                                        <Link
                                            to="/admin"
                                            onClick={() => setDropdownOpen(false)}
                                            className="flex items-center gap-2 px-4 py-2 text-sm text-accent-400 hover:bg-dark-800"
                                        >
                                            <span>🛡️</span>
                                            {t.nav.admin}
                                        </Link>
                                    )}
                                </div>

                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-dark-800"
                                >
                                    <LogOut className="w-4 h-4" />
                                    {t.common.logout}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
}
